import React, { useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import makeStyles from "utils/makeStyles";
import withStyles from "utils/withStyles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import Typography from "@mui/material/Typography";
import TableRow from "@mui/material/TableRow";
import EnhancedTableHead from "components/Common/StaticSortTable/EnhancedTableHead";
import TableFooter from "@mui/material/TableFooter";
import Paper from "@mui/material/Paper";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import TablePaginationActions from "components/Common/TableGenerator/TablePaginationActions";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import Box from "utils/Box";
import Collapse from "@mui/material/Collapse";
import Loader from "../Loader";
import _ from "lodash";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    display: "inline",
  },
  paper: {
    position: "relative",
    width: "100%",
    overflowX: "auto",
  },
  table: {
    minWidth: 750,
  },
  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1,
  },
  tableHead: {
    backgroundColor: "#ECF0F1",
  },
  headCell: {
    fontWeight: "bold",
    fontSize: "12px",
    width: "100%",
    textAlign: "center",
    lineHeight: "12px",
  },
  pagination: {
    fontSize: "10px",
  },
  tableRowSelected: {
    "&.Mui-selected, &.Mui-selected:hover": {
      backgroundColor: "#ECF0F1",
    },
  },
  tableCell: {
    fontSize: "12px",
    marginLeft: "100px",
  },
  selected: {},
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const Row = ({
  isItemSelected,
  record,
  collapsible,
  sensorTable,
  handleClick,
  labelId,
  fields,
  collapsibleHeader,
  collapsibleFields,
  collapsibleFieldKey,
  index,
}) => {
  const classes = useStyles();

  const [collapseOpen, setCollapseOpen] = useState(false);

  return (
    <>
      <StyledTableRow
        hover
        role="checkbox"
        aria-checked={isItemSelected}
        tabIndex={-1}
        key={record.name}
        classes={{ selected: classes.selected }}
        className={classes.tableRowSelected}
        selected={isItemSelected}
        style={{ height: 53 }}
      >
        {collapsible ? (
          <>
            <TableCell padding="checkbox">
              <Checkbox
                color="primary"
                style={{ visibility: sensorTable && "hidden" }}
                onClick={(event) => handleClick(event, record, index)}
                checked={isItemSelected}
                slotProps={{ input: { "aria-labelledby": labelId } }}
              />
            </TableCell>
            <TableCell>
              <IconButton
                aria-label="expand row"
                size="small"
                onClick={() => setCollapseOpen((prevState) => !prevState)}
              >
                {collapseOpen ? (
                  <KeyboardArrowUpIcon />
                ) : (
                  <KeyboardArrowDownIcon />
                )}
              </IconButton>
            </TableCell>
          </>
        ) : (
          <TableCell padding="checkbox">
            <Checkbox
              color="primary"
              style={{ visibility: sensorTable && "hidden" }}
              onClick={(event) => handleClick(event, record, index)}
              checked={isItemSelected}
              slotProps={{ input: { "aria-labelledby": labelId } }}
            />
          </TableCell>
        )}

        {fields
          .filter((x) => x.visible)
          .map((field) => (
            <TableCell key={field.key} className={classes.tableCell} align="left">
              {field.render && typeof field.render === "function"
                ? field.render(record[field.key], record, index)
                : record[field.key]}
            </TableCell>
          ))}
      </StyledTableRow>
      {collapsible && collapseOpen && (
        <TableRow>
          <TableCell
            style={{ paddingBottom: 0, paddingTop: 0 }}
            colSpan={fields.length}
          >
            <Collapse in={collapseOpen} timeout="auto" unmountOnExit>
              <Box margin={1}>
                <Typography variant="h6" gutterBottom component="div">
                  {collapsibleHeader}
                </Typography>
                <Table size="small" aria-label="purchases">
                  <TableHead>
                    <TableRow>
                      {_.map(collapsibleFields, (x) => (
                        <TableCell key={x.key}>
                          <b>{x.columnName}</b>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {_.map(
                      record?.[collapsibleFieldKey],
                      (collapsibleRow, i) => (
                        <TableRow key={i}>
                          {_.map(collapsibleFields, (x) => (
                            <TableCell key={x.key}>
                              {x.render && typeof x.render === "function"
                                ? x.render(collapsibleRow)
                                : collapsibleRow[x.key]}
                            </TableCell>
                          ))}
                        </TableRow>
                      )
                    )}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const TableGenerator = ({ handleSortChange = () => {}, showSelectAll = true, selectedRecords = [],
  fields = [],
  data = [],
  currentPage = 0,
  initialSort = "",
  backendPagination = false,
  disablePagination = true,
  dataCount,
  searchQuery = "",
  onPageChange = () => {},
  onChangeSelected = () => {},
  onRowPerPageChange = () => {},
  rowOnePage = 10,
  radio = false,
  size = "medium",
  sensorTable = false,
  collapsible = false,
  collapsibleFieldKey,
  collapsibleFields,
  collapsibleHeader,
  ...props
}) => {
  const classes = useStyles();
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState(initialSort);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowOnePage);
  const [collapseOpen, setCollapseOpen] = useState(false);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
    handleSortChange(`${isAsc ? "" : "-"}${property}`);
  };

  useEffect(() => {
    setPage(currentPage);
  }, [currentPage]);

  const handleClick = (event, name, index) => {
    const selectedIndex = selectedRecords.findIndex(
      (val) => val.id === name.id
    );
    let newSelected = [];

    if (radio) {
      if (selectedIndex) {
        newSelected = [name];
      } else {
        newSelected = [];
      }
    } else {
      if (selectedIndex === -1) {
        newSelected = newSelected.concat(selectedRecords, name);
      } else if (selectedIndex === 0) {
        newSelected = newSelected.concat(selectedRecords.slice(1));
      } else if (selectedIndex === selectedRecords.length - 1) {
        newSelected = newSelected.concat(selectedRecords.slice(0, -1));
      } else if (selectedIndex > 0) {
        newSelected = newSelected.concat(
          selectedRecords.slice(0, selectedIndex),
          selectedRecords.slice(selectedIndex + 1)
        );
      }
    }
    onChangeSelected(newSelected, index);
  };

  const handleChangePage = (event, newPage, towards) => {
    setPage(newPage);
    onPageChange(newPage, towards);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    onRowPerPageChange(event.target.value);
  };

  const handleSelectAll = (selected) => {
    if (selected) {
      onChangeSelected(data);
    } else {
      onChangeSelected([]);
    }
  };

  const isSelected = (record) =>
    selectedRecords.findIndex((x) => x.id === record.id) !== -1;

  return (
    <div className={classes.root}>
      <Paper
        className={`${classes.paper} ${
          props.stickyTable ? "sticky-table" : ""
        }`}
        style={{ height: props.tableHeight ?? undefined }}
      >
        <Loader visible={props.loader} />
        <Table
          className={classes.table}
          aria-labelledby="tableTitle"
          size={size}
          aria-label="enhanced table"
          style={{ minHeight: 122, height: props.tableHeight ?? undefined }}
        >
          <EnhancedTableHead
            stickyTable={props.stickyTable}
            classes={classes}
            numSelected={selectedRecords.length}
            order={order}
            collapsible={collapsible}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            rowCount={data.length}
            fields={fields}
            data={data}
            showSelectAll={props.showSelectAll}
            selectedRecords={selectedRecords}
            onSelectAll={handleSelectAll}
          />
          <TableBody>
            {data
              .slice(
                backendPagination ? 0 : page * rowsPerPage,
                page * rowsPerPage + rowsPerPage
              )
              .map((record, index) => {
                const isItemSelected = isSelected(record);
                const labelId = `enhanced-table-checkbox-${index}`;

                return (
                  <Row
                    key={record.id ?? record.transactionId ?? index}
                    labelId={labelId}
                    collapsibleFields={collapsibleFields}
                    fields={fields}
                    sensorTable={sensorTable}
                    handleClick={handleClick}
                    collapsibleFieldKey={collapsibleFieldKey}
                    index={index}
                    collapsibleHeader={collapsibleHeader}
                    isItemSelected={isItemSelected}
                    record={record}
                    collapsible={collapsible}
                  />
                );
              })}
            {!props.loader && data.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={100}
                  align="center"
                  style={{ border: 0, padding: "24px" }}
                >
                  <strong> No records found</strong>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          <TableFooter className={props.stickyTable ? "sticky-footer" : ""}>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[3, 5, 7, 8, 10, 25, 50, 100]}
                colSpan={fields.length}
                count={dataCount !== undefined ? dataCount : data.length}
                rowsPerPage={rowsPerPage}
                page={page}
                slotProps={{ select: { "aria-label": "rows per page" } }}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                ActionsComponent={(e) => TablePaginationActions(e, props)}
                labelDisplayedRows={({ from, to, count }) =>
                  props?.minimal
                    ? ""
                    : `${from}-${to} of ${
                        count !== -1 ? count : `more than ${to}`
                      }`
                }
              />
            </TableRow>
          </TableFooter>
        </Table>
      </Paper>
    </div>
  );
};


export default withStyles({}, { withTheme: true })(TableGenerator);
